import React from 'react';
import api from '../../services/api';
import { useAuth } from '../../AuthContext';
import './customFields.css';

// Admin page for user-defined fields — the JobNimbus custom-field interface Trey
// asked for (meeting 2026-08-26, 01:25:50).
//
// Two things it deliberately will not let you do, both for the same reason:
// a stored value points at a field's KEY, and the key is derived from the name
// when the field is created.
//   * The key is never editable. Renaming a field changes its label only, so
//     existing values keep pointing at the right place.
//   * Retiring hides a field without deleting the values already entered — a job
//     from last year should still read correctly.
const ENTITIES = [
  { key: 'Project', label: 'Jobs' },
  { key: 'Contact', label: 'Contacts' },
];


// A 403 from these endpoints has an empty body, so there is nothing to show unless
// the message is written here. Shared by both save paths.
const FORBIDDEN =
  'Only an admin can change custom fields. You are signed in as a User — ask an admin, '
  + 'or sign in with an admin account.';

const errorText = (err, fallback) =>
  err.response?.status === 403
    ? FORBIDDEN
    : err.response?.data?.message || fallback;
const TYPES = [
  { key: 'Text', label: 'Text' },
  { key: 'TextArea', label: 'Paragraph' },
  { key: 'Number', label: 'Number' },
  { key: 'Date', label: 'Date' },
  { key: 'Checkbox', label: 'Yes / No' },
  { key: 'Select', label: 'Dropdown' },
];


// Where each field shows up. Worded per entity, because "new job form" is wrong on
// the Contacts tab and a setting nobody can read is a setting nobody sets.
//
// Hidden still reads and writes over the API — it is hidden from people, not from
// Ground Control, which is the point for a field like gcUuid that an integration
// tracks and nobody should ever type.
const visibilityOptions = (entity) => entity === 'Contact'
  ? [
      { key: 'Detail', label: 'Contact page only' },
      { key: 'Intake', label: 'Contact page + new contact form' },
      { key: 'Hidden', label: 'Hidden (API only)' },
    ]
  : [
      { key: 'Detail', label: 'Project page only' },
      { key: 'Intake', label: 'Project page + new job form' },
      { key: 'Hidden', label: 'Hidden (API only)' },
    ];

function CustomFields() {
  // Every write on this page is Admin-only server-side, and a 403 comes back with an
  // empty body — so without this the page looks fully editable, every control fails,
  // and the only feedback is a generic "could not save".
  //
  // Shown rather than hidden: knowing which fields exist is useful to anyone, and a
  // blank wall would be worse than a read-only view that says why.
  const { isAdmin } = useAuth();
  const canEdit = isAdmin();

  const [entity, setEntity] = React.useState('Project');
  const [fields, setFields] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const [label, setLabel] = React.useState('');
  const [type, setType] = React.useState('Text');
  const [section, setSection] = React.useState('');
  const [visibility, setVisibility] = React.useState('Intake');
  const [optionsText, setOptionsText] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // The fields the model already has. Listed so nobody adds a custom "Gate Code"
  // when Customer has carried one all along — Jobs have 76 built-in fields and
  // Contacts 32, which is well past what anyone holds in their head.
  const [builtIn, setBuiltIn] = React.useState([]);
  const [builtInOpen, setBuiltInOpen] = React.useState(false);
  const [builtInSearch, setBuiltInSearch] = React.useState('');

  // Which built-in field is having its choices edited, and the draft text.
  //
  // A built-in field's TYPE cannot be changed here and this does not pretend to:
  // Invoice Item is a text column in Postgres, and making it an enum would be a
  // migration. What giving it choices does is change the control on the form from a
  // free-text box to a dropdown — the column stays text.
  const [editingBuiltIn, setEditingBuiltIn] = React.useState(null);
  const [builtInDraft, setBuiltInDraft] = React.useState('');
  const [builtInSaving, setBuiltInSaving] = React.useState(false);

  // Editing a custom field's dropdown choices, inline in its row.
  const [editingChoices, setEditingChoices] = React.useState(null);
  const [choicesDraft, setChoicesDraft] = React.useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    api.get('/custom-fields', { params: { entity, includeInactive: true } })
      .then((res) => setFields(res.data || []))
      .catch(() => setError('Could not load custom fields.'))
      .finally(() => setLoading(false));
  }, [entity]);

  React.useEffect(() => { load(); }, [load]);

  const loadBuiltIn = React.useCallback(() => {
    api.get('/custom-fields/built-in', { params: { entity } })
      .then((res) => setBuiltIn(res.data || []))
      .catch(() => setBuiltIn([]));
  }, [entity]);

  React.useEffect(() => { loadBuiltIn(); }, [loadBuiltIn]);

  const builtInFiltered = React.useMemo(() => {
    const q = builtInSearch.trim().toLowerCase();
    if (!q) return builtIn;
    return builtIn.filter((f) =>
      f.label.toLowerCase().includes(q) || f.name.toLowerCase().includes(q));
  }, [builtIn, builtInSearch]);

  // A custom field duplicating a built-in one is the specific mistake this list
  // exists to prevent, so say so at the point of typing rather than after saving.
  const clash = React.useMemo(() => {
    const q = label.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!q) return null;
    return builtIn.find((f) => f.name.toLowerCase() === q || f.label.toLowerCase().replace(/[^a-z0-9]/g, '') === q) || null;
  }, [label, builtIn]);

  const add = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.post('/custom-fields', {
        entity,
        label: label.trim(),
        type,
        section: section.trim() || null,
        visibility,
        options: type === 'Select'
          ? optionsText.split('\n').map((o) => o.trim()).filter(Boolean)
          : null,
      });
      setLabel(''); setOptionsText(''); setSection(''); setVisibility('Intake');
      load();
    } catch (err) {
      setError(errorText(err, 'Could not add that field.'));
    } finally {
      setSaving(false);
    }
  };


  const saveBuiltInOptions = async (field) => {
    setBuiltInSaving(true);
    setError('');
    try {
      await api.put(
        `/custom-fields/built-in/${encodeURIComponent(field.name)}`,
        { options: builtInDraft.split('\n').map((o) => o.trim()).filter(Boolean) },
        { params: { entity } }
      );
      setEditingBuiltIn(null);
      setBuiltInDraft('');
      loadBuiltIn();
      setNotice(`Saved choices for ${field.label}.`);
    } catch (err) {
      setError(errorText(err, 'Could not save those choices.'));
    } finally {
      setBuiltInSaving(false);
    }
  };

  const patch = async (row, changes) => {
    setError('');
    try {
      await api.put(`/custom-fields/${row.id}`, {
        label: row.label,
        type: row.type,
        section: row.section,
        helpText: row.helpText,
        sortOrder: row.sortOrder,
        isActive: row.isActive,
        visibility: row.visibility,
        options: row.options,
        ...changes,
      });
      load();
    } catch (err) {
      // The server's own words, not a generic failure. A refused type change says
      // exactly how many saved values would stop being readable, which is the only
      // thing that tells someone what to do next.
      setError(errorText(err, 'Could not save that change.'));
      // Reloaded so the control snaps back to what is actually stored rather than
      // sitting on a value the server rejected.
      load();
    }
  };


  const saveChoices = async (row) => {
    const options = choicesDraft.split('\n').map((o) => o.trim()).filter(Boolean);
    if (options.length === 0) {
      setError('A dropdown needs at least one choice.');
      return;
    }
    setEditingChoices(null);
    setChoicesDraft('');
    // patch() already carries the rest of the row and reloads, so a choice list
    // cannot be saved alongside a stale label or section.
    await patch(row, { options });
  };
  const retire = async (row) => {
    try {
      const res = await api.delete(`/custom-fields/${row.id}`);
      setNotice(res.data?.message || 'Retired.');
      load();
    } catch {
      setError('Could not retire that field.');
    }
  };

  // Reordering sends the whole list, so the saved order can never be half-applied.
  const move = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[idx], next[target]] = [next[target], next[idx]];
    setFields(next);
    try {
      await api.put('/custom-fields/order', next.map((f) => f.id));
    } catch {
      setError('Could not save the new order.');
      load();
    }
  };

  return (
    <div className="cf-page">
      <h2 className="cf-title">Custom Fields</h2>
      <p className="cf-intro">
        Add your own fields to jobs and contacts without waiting on a developer.
        They appear on the record under the section you give them, in the order set
        here.
      </p>

      {!canEdit && (
        <div className="cf-readonly-note">
          You are signed in as a User, so this page is read-only. Only an admin can add
          or change fields.
        </div>
      )}
      <div className="cf-entity-tabs">
        {ENTITIES.map((e) => (
          <button
            key={e.key}
            type="button"
            className={`cf-tab${entity === e.key ? ' active' : ''}`}
            onClick={() => setEntity(e.key)}
          >
            {e.label}
          </button>
        ))}
      </div>

      {error && <div className="cf-error">{error}</div>}
      {notice && <div className="cf-notice">{notice}</div>}

      <form className="cf-add" onSubmit={add}>
        <div className="cf-add-row">
          <label className="cf-add-field">
            <span>Field name</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Roof Pitch"
              maxLength={100}
              className="form-input"
            />
            {clash && (
              <span className="cf-clash">
                {ENTITIES.find((x) => x.key === entity)?.label} already have a
                built-in “{clash.label}” field — adding this would give you two.
              </span>
            )}
          </label>

          <label className="cf-add-field">
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)} className="form-input">
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>

          <label className="cf-add-field">
            <span>Section (optional)</span>
            <input
              type="text"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              placeholder="e.g. Access"
              maxLength={60}
              className="form-input"
            />
            <small className="cf-add-hint">
              Groups this field under a heading on the project page and the job form.
              Leave blank to sit with the rest.
            </small>
          </label>

          <label className="cf-add-field">
            <span>Shows on</span>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="form-input"
            >
              {visibilityOptions(entity).map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
            </select>
          </label>
        </div>

        {type === 'Select' && (
          <label className="cf-add-field cf-add-options">
            <span>Choices — one per line</span>
            <textarea
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              rows={3}
              placeholder={'Front gate\nSide gate\nNo gate'}
              className="form-input"
            />
          </label>
        )}

        <button type="submit" className="cf-add-btn" disabled={!canEdit || saving || !label.trim()}>
          {saving ? 'Adding…' : 'Add field'}
        </button>
      </form>

      {loading ? (
        <div className="cf-loading">Loading…</div>
      ) : fields.length === 0 ? (
        <div className="cf-empty">No custom fields yet for {ENTITIES.find((e) => e.key === entity)?.label.toLowerCase()}.</div>
      ) : (
        <table className="cf-table">
          <thead>
            <tr>
              <th className="cf-order-col"></th>
              <th>Name</th>
              <th>Type</th>
              <th>Choices</th>
              <th>Section</th>
              <th>Key</th>
              <th>Shows on</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((row, idx) => (
              <tr key={row.id} className={row.isActive ? '' : 'cf-retired'}>
                <td className="cf-order-col">
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up">↑</button>
                  <button type="button" onClick={() => move(idx, 1)} disabled={idx === fields.length - 1} title="Move down">↓</button>
                </td>
                <td>
                  <input
                    type="text"
                    defaultValue={row.label}
                    onBlur={(e) => e.target.value.trim() && e.target.value !== row.label && patch(row, { label: e.target.value.trim() })}
                    className="cf-inline-input"
                    maxLength={100}
                  />
                </td>
                {/* Editable, not a label. The server allows a type change whenever the
                    stored values would still read correctly, and refuses with a count
                    when they would not — so the honest control is a dropdown that can
                    surface that refusal, rather than text implying it is settled. */}
                <td>
                  <select
                    value={row.type}
                    onChange={(e) => patch(row, { type: e.target.value })}
                    disabled={!canEdit}
                    className="cf-inline-input"
                  >
                    {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </td>
                {/* Choices, for a Dropdown. Editable here because "I made it a dropdown
                    and now the list is wrong" had no answer short of deleting the field
                    and losing every value stored against it. */}
                <td className="cf-builtin-options">
                  {row.type !== 'Select' ? (
                    /* Choices only mean anything on a Dropdown. Says which control to
                       change rather than a bare dash, because "there is no way to edit
                       the choices" is what a dash looks like when the reason is one
                       column to the left. */
                    <span className="cf-muted">Set Type to Dropdown</span>
                  ) : editingChoices === row.id ? (
                    <div className="cf-builtin-edit">
                      <textarea
                        value={choicesDraft}
                        onChange={(e) => setChoicesDraft(e.target.value)}
                        rows={4}
                        placeholder={'One choice per line'}
                        className="form-input"
                        autoFocus
                      />
                      <div className="cf-builtin-edit-actions">
                        <button
                          type="button"
                          className="cf-btn"
                          onClick={() => saveChoices(row)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="cf-btn"
                          onClick={() => { setEditingChoices(null); setChoicesDraft(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                  {row.options?.length ? <span className="cf-options-text" title={row.options.join(', ')}>{row.options.join(', ')}</span> : <span className="cf-muted">none yet</span>}
                      <button
                        type="button"
                        className="cf-btn cf-builtin-edit-btn"
                        disabled={!canEdit}
                        onClick={() => {
                          setEditingChoices(row.id);
                          setChoicesDraft((row.options || []).join('\n'));
                        }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    defaultValue={row.section || ''}
                    onBlur={(e) => e.target.value !== (row.section || '') && patch(row, { section: e.target.value.trim() })}
                    className="cf-inline-input"
                    placeholder="—"
                    maxLength={60}
                  />
                </td>
                <td>
                  {/* Shown because it is what a value points at, and what an
                      automation or template will reference once those are wired.
                      Not editable — changing it would orphan stored values. */}
                  <code className="cf-key">{row.key}</code>
                </td>
                <td>
                  <select
                    value={row.visibility || 'Detail'}
                    onChange={(e) => patch(row, { visibility: e.target.value })}
                    disabled={!canEdit}
                    className="cf-inline-input"
                  >
                    {visibilityOptions(entity).map((v) => (
                      <option key={v.key} value={v.key}>{v.label}</option>
                    ))}
                  </select>
                </td>
                <td>{row.isActive ? 'Active' : 'Retired'}</td>
                <td className="cf-actions">
                  {row.isActive ? (
                    <button type="button" className="cf-btn" onClick={() => retire(row)}>Retire</button>
                  ) : (
                    <button type="button" className="cf-btn" onClick={() => patch(row, { isActive: true })}>Restore</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="cf-builtin">
        <button
          type="button"
          className="cf-builtin-toggle"
          onClick={() => setBuiltInOpen((v) => !v)}
        >
          {builtInOpen ? '▾' : '▸'} Built-in fields ({builtIn.length})
        </button>

        {builtInOpen && (
          <>
            <p className="cf-builtin-note">
              Already on the record — shown so you can check before adding a custom
              field that duplicates one. Their types belong to the app, but a text
              field can be given a fixed list of choices, which turns its box on the
              form into a dropdown.
            </p>
            <input
              type="text"
              value={builtInSearch}
              onChange={(e) => setBuiltInSearch(e.target.value)}
              placeholder="Search built-in fields…"
              className="form-input cf-builtin-search"
            />
            <table className="cf-table cf-builtin-table">
              <thead>
                <tr><th>Name</th><th>Type</th><th>Choices</th><th>Stored as</th></tr>
              </thead>
              <tbody>
                {builtInFiltered.map((f) => (
                  <tr key={f.name} className={f.isReference ? 'cf-builtin-ref' : ''}>
                    <td>{f.label}</td>
                    <td>{f.type}</td>
                    <td className="cf-builtin-options">
                      {editingBuiltIn === f.name ? (
                        <div className="cf-builtin-edit">
                          <textarea
                            value={builtInDraft}
                            onChange={(e) => setBuiltInDraft(e.target.value)}
                            rows={4}
                            placeholder={'One choice per line\nLeave empty for a free-text box'}
                            className="form-input"
                            autoFocus
                          />
                          <div className="cf-builtin-edit-actions">
                            <button
                              type="button"
                              className="cf-btn"
                              disabled={builtInSaving}
                              onClick={() => saveBuiltInOptions(f)}
                            >
                              {builtInSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              className="cf-btn"
                              onClick={() => { setEditingBuiltIn(null); setBuiltInDraft(''); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : f.configuredOptions && f.configuredOptions.length ? (
                        <>
                          <span className="cf-options-text" title={f.configuredOptions.join(', ')}>{f.configuredOptions.join(', ')}</span>
                          <button
                            type="button"
                            className="cf-btn cf-builtin-edit-btn"
                            disabled={!canEdit}
                            onClick={() => {
                              setEditingBuiltIn(f.name);
                              setBuiltInDraft(f.configuredOptions.join('\n'));
                            }}
                          >
                            Edit
                          </button>
                        </>
                      ) : f.options && f.options.length ? (
                        /* The column's own values. Read-only on purpose: these are ints
                           in Postgres and names in C#, so adding or removing one is a
                           code change and a migration, not a setting. Said out loud in
                           the cell, because an unexplained missing button reads as
                           broken rather than deliberate. */
                        <>
                          <span className="cf-options-text" title={f.options.join(', ')}>{f.options.join(', ')}</span>
                          <span className="cf-muted cf-set-in-code">set in code</span>
                        </>
                      ) : f.isReference ? (
                        'link to another record'
                      ) : f.canConfigureOptions ? (
                        <button
                          type="button"
                          className="cf-btn cf-builtin-edit-btn"
                          disabled={!canEdit}
                          onClick={() => { setEditingBuiltIn(f.name); setBuiltInDraft(''); }}
                        >
                          Add choices
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td><code className="cf-key">{f.name}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {builtInFiltered.length === 0 && (
              <div className="cf-empty">No built-in field matches “{builtInSearch}”.</div>
            )}
          </>
        )}
      </div>

      <p className="cf-footnote">
        Custom fields are not yet available as automation conditions or email/document
        template variables — that wiring is a separate step.
      </p>
    </div>
  );
}

export default CustomFields;
